import React, { useMemo, useState } from "react"
import useSWR from "swr"
import { cancelClient, FETCH_KEYS, fetchClients } from "../api"
import { DataTable, DataTableColumn } from "mantine-datatable"
import { ActionIcon, Box, Button, Group, PasswordInput, ScrollArea, Stack, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core"
import { BuildingsIcon, MagnifyingGlassIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import BigSpinning from "./BigSpinning"
import { TablePagination } from "../components/TablePagination"
import { notifications } from "@mantine/notifications"
import { BaseModal } from "../components/BaseModal"
import { ClientCreateModal } from "../components/ClientCreateModal"

interface ClientRow {
  id: number
  name: string
  url_name: string
  created_at: string
  last_admin_login_at: string | null
  cancel_date: string | null
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  return value.substring(0, 10)
}

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  return value.substring(0, 16).replace('T', ' ')
}

const ClientsContainer = () => {
  // index は HTTP Basic 認証のため、Bearer を送る fetcherWithAuth は使えない
  const { data, error, isValidating, mutate } = useSWR<ClientRow[]>(
    FETCH_KEYS.clients,
    fetchClients,
    { revalidateOnFocus: false },
  )

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [canceling, setCanceling] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [cancelTarget, setCancelTarget] = useState<ClientRow | null>(null)
  const [cancelOpened, setCancelOpened] = useState(false)
  const [cancelPassword, setCancelPassword] = useState('')
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [createOpened, setCreateOpened] = useState(false)

  const items = data ?? []

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return items

    return items.filter(
      (row) =>
        String(row.id).includes(q) ||
        row.name.toLowerCase().includes(q) ||
        row.url_name.toLowerCase().includes(q),
    )
  }, [items, searchQuery])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage))
  const currentPage = Math.min(page, totalPages)
  const pagedItems = filteredItems.slice((currentPage - 1) * perPage, currentPage * perPage)

  const handleChangePerPage = (next: number) => {
    setPerPage(next)
    setPage(1)
  }

  const handleChangeSearch = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  // 閉じるアニメーション中もクライアントが消えないよう、cancelTarget は残したまま opened だけ倒す
  const handleClickCancel = (client: ClientRow) => {
    setCancelTarget(client)
    setCancelOpened(true)
  }

  const handleCloseCancel = () => {
    if (canceling) return
    setCancelOpened(false)
  }

  const handleSubmitCancel = async () => {
    if (!cancelTarget) return

    setCanceling(true)
    setCancelError(null)

    try {
      await cancelClient(cancelTarget.id, cancelPassword)
      setCancelOpened(false)
      await mutate()
      notifications.show({ message: 'クライアントを退会させました', color: 'teal' })
    } catch (e) {
      console.error('クライアントの退会に失敗しました', e)
      setCancelError('失敗しました')
    } finally {
      setCanceling(false)
    }
  }

  const columns: DataTableColumn<ClientRow>[] = [
    { accessor: 'id', title: 'ID', width: 80 },
    { accessor: 'name', title: 'クライアント名' },
    { accessor: 'url_name', title: 'URL名', width: 180 },
    {
      accessor: 'created_at',
      title: '作成日時',
      width: 130,
      render: (row) => formatDate(row.created_at),
    },
    {
      accessor: 'last_admin_login_at',
      title: '管理画面最終ログイン日時',
      width: 200,
      render: (row) => formatDateTime(row.last_admin_login_at),
    },
    {
      accessor: 'cancel_date',
      title: '退会日時',
      width: 120,
      render: (row) => row.cancel_date || '-',
    },
    {
      accessor: 'cancel',
      title: '退会処理',
      width: 100,
      textAlign: 'center',
      render: (row) =>
        row.cancel_date ? (
          '-'
        ) : (
          <Tooltip label="退会">
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleClickCancel(row)}
            >
              <TrashIcon size={18} />
            </ActionIcon>
          </Tooltip>
        ),
    },
  ]

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ高さを固定する
  const tableHeight =
    pagedItems.length === 0 ? 400 : pagedItems.length >= 15 ? 600 : undefined

  if (error) return <Text>クライアント一覧の取得に失敗しました</Text>
  if (!data) return <BigSpinning />

  return (
    <Box>
      <Group justify="space-between" align="center" mb={40}>
        <Group gap={12}>
          <ThemeIcon variant="transparent" color="invy">
            <BuildingsIcon weight="fill" size={36} />
          </ThemeIcon>
          <Title size={24} fw={600} c="#000000CC">
            クライアント一覧
          </Title>
        </Group>

        <Button
          variant="filled"
          color="invy"
          radius="xl"
          leftSection={<PlusIcon size={16} weight="bold" />}
          onClick={() => setCreateOpened(true)}
        >
          新規クライアント
        </Button>
      </Group>

      <TextInput
        placeholder="ID・クライアント名・URL名で検索"
        leftSection={<MagnifyingGlassIcon size={16} />}
        value={searchQuery}
        onChange={(e) => handleChangeSearch(e.currentTarget.value)}
        w={320}
        mb={16}
      />

      <Box
        style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden' }}
      >
        <DataTable
          withColumnBorders
          records={pagedItems}
          columns={columns}
          height={tableHeight}
          fetching={isValidating || canceling}
          noRecordsText="該当するクライアントがありません"
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#F8F9FA' },
          }}
        />

        <TablePagination
          page={currentPage}
          onChangePage={setPage}
          perPage={perPage}
          onChangePerPage={handleChangePerPage}
          totalPages={totalPages}
          isEmpty={pagedItems.length === 0}
        />
      </Box>

      {cancelTarget && (
        <BaseModal
          opened={cancelOpened}
          onClose={handleCloseCancel}
          title="クライアントの退会"
          width={600}
          onExited={() => {
            setCancelPassword('')
            setCancelError(null)
          }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008">
            <Stack gap="sm" p="md">
              <Text>
                本当に {cancelTarget.name} を退会させますか？退会用PWを入力してください
              </Text>
              <PasswordInput
                value={cancelPassword}
                onChange={(e) => {
                  setCancelPassword(e.currentTarget.value)
                  setCancelError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && cancelPassword && !canceling) {
                    handleSubmitCancel()
                  }
                }}
                error={cancelError}
                autoComplete="off"
              />
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{
              borderTop: '1px solid #0000001A',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="outline"
                radius="xl"
                color="gray"
                onClick={handleCloseCancel}
                disabled={canceling}
              >
                キャンセル
              </Button>
              <Button
                variant="filled"
                color="invy"
                radius="xl"
                onClick={handleSubmitCancel}
                disabled={!cancelPassword}
                loading={canceling}
              >
                退会させる
              </Button>
            </Group>
          </Box>
        </BaseModal>
      )}

      <ClientCreateModal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        onSaved={() => mutate()}
      />
    </Box>
  )
}

export default ClientsContainer
